import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import _ from 'lodash';

import { CmsMenu } from '../../models/content';
import { ModalService } from '../../services/modal.service';

interface EditableChild {
  id: string;
  label: string;
  target: string;
}

interface EditableItem {
  id: string;
  label: string;
  target: string;
  /** Non-empty → this row is a section heading and `target` is ignored. */
  children: EditableChild[];
}

/**
 * Menu group editor — name, header/footer, and the ordered links. Links are
 * a local array rebuilt into the id-keyed map on save (ids preserved).
 */
@Component({
  selector: 'app-content-menu',
  templateUrl: './content-menu.component.html',
})
export class ContentMenuComponent implements OnInit {
  menu!: CmsMenu;
  isNew = false;
  form!: FormGroup;
  items: EditableItem[] = [];
  locationOptions = [
    { label: 'Header menu', value: 'header' },
    { label: 'Footer menu', value: 'footer' },
  ];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.menu = this.config.data.menu;
    this.isNew = !!this.config.data.isNew;

    this.form = this.fb.group({
      name: new FormControl(this.menu.name ?? '', [Validators.required, Validators.pattern(/\S/)]),
      location: new FormControl(this.menu.location ?? 'header', [Validators.required]),
      showRegions: new FormControl(this.menu.showRegions === true),
    });

    const raw: any = this.menu.items;
    const list: any[] = !raw ? [] : Array.isArray(raw) ? raw.filter(Boolean) : Object.values(raw).filter(Boolean);
    this.items = _.sortBy(list, (it) => it?._meta?.index ?? Number.MAX_SAFE_INTEGER).map((it, i) => {
      const rawKids: any = it?.children;
      const kidList: any[] = !rawKids
        ? []
        : Array.isArray(rawKids)
          ? rawKids.filter(Boolean)
          : Object.values(rawKids).filter(Boolean);
      return {
        id: it?._meta?.id || `id-i${(i + 1) * 10}`,
        label: it?.label ?? '',
        target: it?.target ?? '',
        children: _.sortBy(kidList, (c) => c?._meta?.index ?? Number.MAX_SAFE_INTEGER).map((c, k) => ({
          id: c?._meta?.id || `id-c${(k + 1) * 10}`,
          label: c?.label ?? '',
          target: c?.target ?? '',
        })),
      };
    });
  }

  addItem(): void {
    this.items.push({ id: `id-i${this.shortId()}`, label: '', target: '', children: [] });
  }

  /**
   * Seed the menu label from the page just picked (#199), only when it is
   * still empty — menu wording is deliberately shorter than a page title
   * ("Costs", not "Funeral costs and what shapes them"), so a typed label
   * always wins.
   */
  applyPickedLabel(row: { label?: string }, picked: { title?: string }): void {
    if (!row || !picked?.title) return;
    if ((row.label || '').trim()) return;
    row.label = picked.title;
  }

  addChild(item: EditableItem): void {
    item.children.push({ id: `id-c${this.shortId()}`, label: '', target: '' });
  }
  removeChild(item: EditableItem, i: number): void {
    item.children.splice(i, 1);
  }
  moveChild(item: EditableItem, i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= item.children.length) return;
    const [c] = item.children.splice(i, 1);
    item.children.splice(j, 0, c);
  }
  removeItem(i: number): void {
    this.items.splice(i, 1);
  }
  moveItem(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.items.length) return;
    const [it] = this.items.splice(i, 1);
    this.items.splice(j, 0, it);
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const kept = this.items
      .map((it) => ({
        ...it,
        label: (it.label || '').trim(),
        target: (it.target || '').trim(),
        children: (it.children || [])
          .map((c) => ({ ...c, label: (c.label || '').trim(), target: (c.target || '').trim() }))
          .filter((c) => c.label && c.target),
      }))
      // A row needs a link of its own OR sub-links; anything else renders nothing.
      .filter((it) => it.label && (it.target || it.children.length > 0));
    if (!kept.length) {
      this.modalService.generic({
        title: "This menu can't be saved yet",
        copy: ['Add at least one link (both a label and a target address) — an empty menu renders nothing.'],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }
    const targets = kept.flatMap((it) => [
      ...(it.target ? [{ label: it.label, target: it.target }] : []),
      ...it.children.map((c) => ({ label: c.label, target: c.target })),
    ]);
    const bad = targets.find((t) => !t.target.startsWith('/') && !/^https?:\/\//.test(t.target));
    if (bad) {
      this.modalService.generic({
        title: "This menu can't be saved yet",
        copy: [`"${bad.label}" points at "${bad.target}" — link targets must start with "/" (a page on the site) or be a full https:// address.`],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    const items: Record<string, any> = {};
    kept.forEach((it, i) => {
      let id = it.id;
      while (items[id]) id = `${id}-${i}`;
      const children: Record<string, any> = {};
      it.children.forEach((c, k) => {
        let cid = c.id;
        while (children[cid]) cid = `${cid}-${k}`;
        children[cid] = { _meta: { id: cid, index: k * 10 }, label: c.label, target: c.target };
      });
      items[id] = {
        _meta: { id, index: i * 10 },
        label: it.label,
        // A heading stores no target; the site treats it as a section.
        target: it.children.length ? '' : it.target,
        ...(it.children.length ? { children } : {}),
      };
    });

    const value = this.form.getRawValue();
    this.ref.close({
      saved: true,
      menu: {
        id: this.menu._meta.id,
        name: value.name.trim(),
        location: value.location,
        showRegions: value.showRegions === true,
        items,
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }

  private shortId(): string {
    return Math.random().toString(36).slice(2, 8);
  }
}
